import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { CONTACT, MARKETING_CONTACT_DEPRECATED, USER } from 'self-service-api/constants/UnitTypes';
import { useAppSelector } from '../../reducers';
import { productVersionFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { currentSkuIdForProductSelector, isSalesOrServiceProOrEntSkuSelector, productForSkuIdSelector, skuSelector } from '../../selectors/ProductsSelectors';
import { usePricingLibSelector } from '../../selectors/SelectorUtils';
import ContactsPicker from '../common/quantityPacks/ContactsPicker';
import SeatsPicker from '../common/quantityPacks/SeatsPicker';

const AdditionalUnitsPicker = ({
  additionalUnitSkuId,
  additionalUnitQuantity,
  setAdditionalUnitQuantity,
  productConfigurations,
  setActiveProductLevel,
  compact = false,
  trackingSource,
  queryParamTerm,
  setProductConfigurations
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const unitType = useAppSelector(state => {
    if (!additionalUnitSkuId) {
      return null;
    }

    const sku = skuSelector(state, {
      skuId: additionalUnitSkuId
    });
    return sku && sku.quantityUnit && sku.quantityUnit.unitType;
  });
  const isSalesOrServiceProOrEntSku = useAppSelector(state => additionalUnitSkuId && isSalesOrServiceProOrEntSkuSelector(state, {
    skuId: additionalUnitSkuId
  }));
  const onContactQuantityChange = useAppSelector(state => quantity => {
    if (!additionalUnitSkuId) return;
    const {
      id: currentBaseProductId
    } = productForSkuIdSelector(state, {
      skuId: additionalUnitSkuId
    });
    const currentBaseSkuId = currentSkuIdForProductSelector(state, {
      productId: currentBaseProductId
    });
    const ownedProductVersion = productVersionFromProductInventorySelector(state, {
      productAttributeFilters: {
        productId: currentBaseProductId
      }
    });
    const ownedProductSkuId = ownedProductVersion && ownedProductVersion.skuId; // If we're on a legacy Marketing SKU, we need to enforce the
    // base current SKU is added anytime quantity packs are

    if (currentBaseSkuId && currentBaseSkuId !== ownedProductSkuId && setProductConfigurations) {
      setProductConfigurations(() => {
        // This step removes duplicates
        const filteredConfigs = productConfigurations.filter(({
          skuId: configSkuId
        }) => configSkuId !== currentBaseSkuId && configSkuId !== additionalUnitSkuId); // Add the appropriate base SKU

        filteredConfigs.push(createProductConfig({
          skuId: currentBaseSkuId,
          quantity: 1
        })); // Add the appropriate quantityPack SKU

        if (quantity > 0) {
          filteredConfigs.push(createProductConfig({
            skuId: additionalUnitSkuId,
            quantity
          }));
        }

        return filteredConfigs;
      });
    } else {
      setAdditionalUnitQuantity(quantity, additionalUnitSkuId);
    }
  });

  const renderUnitsPicker = () => {
    if (unitType === USER || isSalesOrServiceProOrEntSku && isSeatsRedesignUngated) {
      return /*#__PURE__*/_jsx(SeatsPicker, {
        additionalUnitSkuId: additionalUnitSkuId,
        additionalUnitQuantity: additionalUnitQuantity,
        onChange: setAdditionalUnitQuantity,
        productConfigurations: productConfigurations,
        compact: compact,
        trackingSource: trackingSource,
        queryParamTerm: queryParamTerm
      });
    } else if (unitType === CONTACT || unitType === MARKETING_CONTACT_DEPRECATED) {
      return /*#__PURE__*/_jsx(ContactsPicker, {
        quantityPackSkuId: additionalUnitSkuId,
        onChange: onContactQuantityChange,
        quantityPackQuantity: additionalUnitQuantity,
        productConfigurations: productConfigurations,
        compact: compact,
        setProductLevel: setActiveProductLevel,
        trackingSource: trackingSource,
        queryParamTerm: queryParamTerm
      });
    }

    return null;
  };

  return /*#__PURE__*/_jsx(_Fragment, {
    children: additionalUnitSkuId && renderUnitsPicker()
  });
};

export default AdditionalUnitsPicker;